"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Calendar, Euro, MapPin } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
const PINNED_POST_IDS = ["1122", "1006", "1142", "1160", "1173", "1131"];
function WorkshopGrid() {
  const {
    posts: allPosts,
    loading,
    error
  } = usePosts();
  const posts = React.useMemo(() => {
    if (!allPosts) return [];
    const filtered = allPosts.filter(post => post.categories?.some(cat => cat.id === 249 || cat === 249));
    const pinnedMap = new Map(filtered.map(p => [String(p.id), p]));
    const pinnedPosts = PINNED_POST_IDS.map(id => pinnedMap.get(id)).filter((p): p is (typeof filtered)[number] => p !== undefined);
    const pinnedIdSet = new Set(PINNED_POST_IDS);
    const remainingPosts = filtered.filter(p => !pinnedIdSet.has(String(p.id)));
    return [...pinnedPosts, ...remainingPosts];
  }, [allPosts]);
  if (loading) {
    return <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="flex h-[400px] flex-col rounded-xl bg-muted/30 animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="mt-12 text-center text-muted-foreground">
        <p>Seminare konnten nicht geladen werden. Bitte versuchen Sie es später erneut.</p>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="mt-12 text-center text-muted-foreground">
        <p>Zurzeit sind keine Seminare geplant. Schauen Sie bald wieder vorbei!</p>
      </div>;
  }
  return <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, i) => <div key={post.id} data-index={i}>
          <Card className="flex h-full flex-col overflow-hidden border-border shadow-sm transition-shadow hover:shadow-md">
            <div className="relative aspect-video w-full overflow-hidden bg-muted">
              <img src={post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1587884/img/workshop_placeholder.png"} alt={post.title} className="h-full w-full object-cover" />
            </div>
            <CardHeader className="pb-4">
              <CardTitle className="text-xl text-primary">{post.title}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-grow flex-col gap-4">
              <div className="flex flex-col gap-2 text-sm text-muted-foreground">
                {post.customFields?.date && <div className="flex items-center gap-2">
                    <SafeHydrate><Calendar className="size-4 shrink-0" /></SafeHydrate>
                    <span>{post.customFields.date}</span>
                  </div>}
                {post.customFields?.location && <div className="flex items-center gap-2">
                    <MapPin className="size-4 shrink-0" />
                    <span>{post.customFields.location}</span>
                  </div>}
                {post.customFields?.price !== undefined && <div className="flex items-center gap-2">
                    <Euro className="size-4 shrink-0" />
                    <span>{post.customFields.price}</span>
                  </div>}
              </div>
              <p className="line-clamp-3 text-sm text-muted-foreground">
                {post.customFields?.description || post.excerpt?.replace(/(<([^>]+)>)/gi, "")}
              </p>
            </CardContent>
            <CardFooter className="pt-0">
              <Link to={post.link || "#"} className="w-full">
                <Button variant="outline" className="w-full border-primary text-primary hover:bg-primary/5">
                  Mehr erfahren
                </Button>
              </Link>
            </CardFooter>
          </Card>
        </div>)}
    </div>;
}
export default function AktuelleSeminare() {
  const wp_query_params = {
    per_page: 20,
    orderby: "date",
    order: "asc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="aktuelle-seminare" className="bg-background py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center md:text-left">
          <h2 className="text-3xl font-bold tracking-tight text-primary sm:text-4xl">Aktuelle Seminare</h2>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground">Entdecke unsere kommenden Workshops und vertiefe Deine Praxis an intensiven Tagen.</p>
        </div>

        <PostsProvider wp_query={{
        per_page: 20,
        orderby: "date",
        order: "asc",
        status: ["publish"]
      }}>
          <WorkshopGrid />
        </PostsProvider>
      </div>
    </section>;
}